import React, { useEffect, useState } from "react";
import "./payment-security.css";
import usePageMeta from "../../../hooks/usePageMeta";

export default function PaymentSecurity() {
  usePageMeta(
  "Payment Security | MakeBeeTrip",
  "How MakeBeeTrip protects your online payments: secure checkout, encrypted transactions and trusted payment gateway partners for every booking.",
  "makebeetrip payment security, secure payments, safe online booking, payment gateway, encrypted transactions, secure checkout"
);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const securitySections = [
    {
      title: "Secure Socket Layer (SSL) Technology",
      content: (
        <>
          <p>
            MakeBeeTrip uses <strong>Secured Socket Layer (SSL) technology by
            VeriSign</strong> to help secure information transmitted between
            your browser and our website.
          </p>

          <p>
            SSL technology encrypts information during transmission and helps
            protect it from unauthorized access while being transmitted over
            the internet.
          </p>

          <p>
            When making a payment or entering sensitive information, customers
            should ensure that the website is accessed through a secure{" "}
            <strong>HTTPS connection</strong>.
          </p>
        </>
      ),
    },

    {
      title: "Payment Gateway",
      content: (
        <>
          <p>
            For online bookings,{" "}
            <strong>
              MakeBeeTrip uses third-party payment gateway services to process
              payments securely
            </strong>
            .
          </p>

          <p>
            When you proceed with a payment, you may be redirected to or
            connected with the secure payment page of the applicable payment
            gateway. The payment gateway processes the transaction through its
            own secure systems and applicable security standards.
          </p>

          <p>
            MakeBeeTrip may receive transaction-related information such as
            payment status, transaction ID, booking reference and payment
            amount. Sensitive payment credentials may be handled directly by
            the payment gateway or relevant financial institution.
          </p>
        </>
      ),
    },

    {
      title: "Secure Payment Processing",
      content: (
        <>
          <p>
            Depending on the payment method selected, the payment gateway may
            process:
          </p>

          <ul>
            <li>Credit Card</li>
            <li>Debit Card</li>
            <li>UPI</li>
            <li>Net Banking</li>
            <li>Other supported online payment methods</li>
          </ul>

          <p>
            Customers should follow the security instructions provided by the
            payment gateway during the transaction.
          </p>
        </>
      ),
    },

    {
      title: "Important Security Notice",
      important: true,
      content: (
        <>
          <p>
            <strong>
              MakeBeeTrip will never ask you to share your confidential payment
              authentication information
            </strong>
            , including:
          </p>

          <ul>
            <li>UPI PIN</li>
            <li>ATM PIN</li>
            <li>Card PIN</li>
            <li>Internet Banking Password</li>
            <li>Payment OTP</li>
          </ul>

          <p>
            Never share these details with anyone, even if they claim to be a
            MakeBeeTrip representative.
          </p>
        </>
      ),
    },

    {
      title: "Payment Confirmation",
      content: (
        <>
          <p>
            Once the payment has been successfully processed, you may receive a
            payment confirmation and/or booking confirmation through the mobile
            number or email address provided during the booking process.
          </p>

          <p>
            Please note that{" "}
            <strong>
              successful payment does not necessarily mean that the travel
              booking is confirmed
            </strong>
            .
          </p>

          <p>
            Final booking confirmation is subject to availability and
            confirmation from the relevant airline, hotel, transportation
            provider, visa service provider or other travel supplier.
          </p>
        </>
      ),
    },

    {
      title: "Failed or Pending Transactions",
      content: (
        <>
          <p>
            If your payment has been deducted but your booking has not been
            confirmed, please contact MakeBeeTrip before making another
            payment.
          </p>

          <p>
            Please keep your{" "}
            <strong>
              transaction ID, payment reference or booking reference
            </strong>{" "}
            available so our team can check the transaction status.
          </p>
        </>
      ),
    },

    {
      title: "Refunds",
      content: (
        <>
          <p>
            If a refund is applicable, it will be processed according to the
            applicable booking terms, cancellation policy and refund conditions
            of the relevant travel service provider.
          </p>

          <p>
            The time taken for the refund to reflect in your account may depend
            on the payment gateway, bank and other financial institutions
            involved in the transaction.
          </p>
        </>
      ),
    },

    {
      title: "Protect Yourself From Online Fraud",
      content: (
        <>
          <p>For your security:</p>

          <ul>
            <li>Make payments only through official MakeBeeTrip payment channels.</li>
            <li>
              Check that you are using the official MakeBeeTrip website before
              making a payment.
            </li>
            <li>Do not share OTPs, UPI PINs, card PINs or banking passwords.</li>
            <li>
              Do not make payments to unknown individuals or unauthorized
              accounts.
            </li>
            <li>
              Do not click suspicious payment links received from unknown
              sources.
            </li>
            <li>Contact us if you receive a suspicious payment request.</li>
          </ul>
        </>
      ),
    },

    {
      title: "Contact Us",
      contact: true,
      content: (
        <div className="payment-security-contact">
          <p>
            For payment-related assistance or to report a suspicious payment
            request, please contact:
          </p>

          <div className="payment-security-contact-card">
            <p>
              <strong>MakeBeeTrip Private Limited</strong>
            </p>

            <p>
              <strong>Phone:</strong>{" "}
              <a href="tel:9355471999">9355471999</a>
            </p>

            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:Holidays@makebeetrip.com">
                Holidays@makebeetrip.com
              </a>
            </p>

            <p>
              <strong>Website:</strong>{" "}
              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noreferrer"
              >
                https://makebeetrip.com/
              </a>
            </p>

            <div className="payment-security-address">
              <strong>Registered/Mailing Address:</strong>
              <span>
                WZ A-1/1, G/F R/P, Vikas Puri, New Delhi, West Delhi – 110018,
                Delhi, India
              </span>
            </div>

            <p>
              <strong>CIN:</strong> U79110DC2026PTC471178
            </p>
          </div>

          <div className="payment-security-tagline">
            <strong>MakeBeeTrip</strong>
            <span>Your Journey, Our Commitment.</span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="payment-security-page">
      {/* Header */}
      <header className="payment-security-header">
        <div className="payment-security-header-inner">
          <button
            onClick={() => window.history.back()}
            className="payment-security-back-button"
            aria-label="Go back"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>

          <div className="payment-security-brand-pill">
            <span>MakeBeeTrip</span>

            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="payment-security-hero">
        <div className="payment-security-hero-glow" />

        <div className="payment-security-hero-content">
          <span className="payment-security-hero-label">
            MakeBeeTrip Private Limited
          </span>

          <h1>Payment Security</h1>

          <p className="payment-security-hero-subtitle">
            Your Security Is Our Priority
          </p>

          <p className="payment-security-hero-description">
            We take reasonable measures to help protect information transmitted
            through our website and support secure payment processing through
            applicable third-party payment gateway services.
          </p>
        </div>
      </section>

      {/* Main */}
      <main className="payment-security-main">
        <section className="payment-security-intro">
          <p>
            At <strong>MakeBeeTrip</strong>, we understand that secure payment
            processing is an important part of your online travel booking
            experience. We take reasonable measures to help protect the
            information transmitted through our website.
          </p>
        </section>

        {/* Desktop */}
        <div className="payment-security-desktop-sections">
          {securitySections.map((section, index) => (
            <article
              key={index}
              className={`payment-security-section-card ${
                section.important ? "payment-security-important-card" : ""
              } ${
                section.contact ? "payment-security-contact-section" : ""
              }`}
            >
              <div className="payment-security-section-heading">
                <span className="payment-security-section-number">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <h2>{section.title}</h2>
              </div>

              <div className="payment-security-section-content">
                {section.content}
              </div>
            </article>
          ))}
        </div>

        {/* Mobile */}
        <div className="payment-security-mobile-sections">
          {securitySections.map((section, index) => {
            const isOpen = openAccordion === index;

            return (
              <article
                key={index}
                className={`payment-security-mobile-card ${
                  section.important
                    ? "payment-security-important-card"
                    : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleAccordion(index)}
                  className="payment-security-mobile-trigger"
                  aria-expanded={isOpen}
                >
                  <span className="payment-security-mobile-title">
                    <span className="payment-security-section-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <span>{section.title}</span>
                  </span>

                  <svg
                    className={`payment-security-chevron ${
                      isOpen ? "payment-security-chevron-open" : ""
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                <div
                  className={`payment-security-mobile-content ${
                    isOpen
                      ? "payment-security-mobile-content-open"
                      : ""
                  }`}
                >
                  <div>{section.content}</div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Final Disclaimer */}
        <div className="payment-security-final-disclaimer">
          <p>
            Payment processing is facilitated through third-party payment
            gateway/service providers and may also be subject to the terms,
            conditions and security policies of the applicable payment gateway,
            bank or financial institution.
          </p>
        </div>
      </main>

      {/* Back To Top */}
      <button
        type="button"
        onClick={scrollToTop}
        className={`payment-security-back-to-top ${
          showScrollTop
            ? "payment-security-back-to-top-visible"
            : ""
        }`}
        aria-label="Back to Top"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      </button>
    </div>
  );
}